import { Paging, parseISODateAndformatDate } from '../../utils/miscellaneous';
import { PaginationComponent } from 'components/Pagination';
import { useQuotationsLazyQuery, QuotationsResultFragment, SortOrder } from 'features/graphql';
import React, { useEffect, useState } from 'react';
import { Alert, Form, Col, Container, Row, Table, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { QUOTATIONS_PATH } from 'route/paths';
import { Loading } from 'components/Loading';
import { graphQlErrorMessage } from 'utils/miscellaneous';

function detailSummary(summary: NonNullable<QuotationsResultFragment['quotations']>[0]['summary']) {
  if (!summary) {
    return '';
  }

  if (1 === summary.detailCount) {
    return summary.firstDetail.medicineName;
  }

  return `${summary.firstDetail.medicineName} 他あり`;
}

type MedicineType = '外' | '注' | '内' | '歯';
export const QuotationList = () => {
  const [paging, setPaging] = useState<Paging>({ page: 0, pageSize: 10 });
  const [medicineName, setMedicineName] = useState('');
  const [capacity, setCapacity] = useState('');
  const [medicineTypes, setMedicineTypes] = useState<MedicineType[]>(['外', '注', '内']);
  const [manufacturer, setManufacturer] = useState('');
  const [genericName, setGenericName] = useState('');
  const [janCode, setJanCode] = useState('');
  const [yjCode, setYjCode] = useState('');
  const [gs1Code, setGs1Code] = useState('');
  const [medicineCategory, setMedicineCategory] = useState('');
  const [searchOperation, setSearchOperation] = useState<'and' | 'or'>('and');

  const [qoutationQuery, { data, loading, error }] = useQuotationsLazyQuery();
  const [sortOrder, setSortOrder] = useState<SortOrder>(SortOrder.Desc);

  const setSessionPaging = (page: number, pageSize: number) => {
    setPaging({ page, pageSize });
  };

  const handleSearch = () => {
    const variables: Record<string, unknown> = {
      ...paging,
      medicineTypes: medicineTypes.length > 0 ? medicineTypes : undefined,
      sortOrder,
      searchOperation,
    };
    if (medicineName.trim()) variables.medicineName = medicineName.trim();
    if (capacity.trim()) variables.capacity = capacity.trim();
    if (manufacturer.trim()) variables.salesManufacturer = manufacturer.trim();
    if (genericName.trim()) variables.commonName = genericName.trim();
    if (janCode.trim()) variables.janCode = janCode.trim();
    if (yjCode.trim()) variables.yjCode = yjCode.trim();
    if (gs1Code.trim()) variables.gs1Code = gs1Code.trim();
    if (medicineCategory.trim()) variables.drugCategoryName = medicineCategory.trim();
    qoutationQuery({ variables });
  };

  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paging, sortOrder, medicineTypes]);

  const onTypeChange = (medicineType: MedicineType, on: boolean) => {
    let newSelectedTypes: MedicineType[];
    if (on) {
      newSelectedTypes = [...medicineTypes, medicineType];
    } else {
      newSelectedTypes = medicineTypes.filter((t) => t !== medicineType);
    }
    setMedicineTypes(newSelectedTypes);
    setPaging({ page: 0, pageSize: paging.pageSize });
  };

  const handleSortOrderChange = (newSortOrder: SortOrder) => {
    setSortOrder(newSortOrder);
    setSessionPaging(0, paging.pageSize);
  };

  const quotations = data?.quotations?.quotations ?? [];

  return (
    <Container fluid className="mt-5 px-5 pb-4 sheet" style={{ marginBottom: '10rem' }}>
      <Row></Row>
      <Row className="mt-4">
        <Col xs={4}>
          <span className="fs-3">依頼履歴</span>
        </Col>
        <Col xs={8} className="text-end">
          <span>
            お問合せは見積番号を記載の上、
            <span className="text-success">info-gpo@sogo-medical.co.jp</span>{' '}
            までメールにてお問い合わせください。
          </span>
        </Col>
      </Row>

      <Row className="mt-2">
        <Col>
          <div className="search p-3 bg-light rounded">
            <div className="row g-3 mb-3">
              <div className="col">
                <Form.Control
                  type="text"
                  placeholder="品名"
                  value={medicineName}
                  onChange={(e) => setMedicineName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="rounded-pill"
                />
              </div>
              <div className="col">
                <Form.Control
                  type="text"
                  placeholder="規格容量"
                  value={capacity}
                  onChange={(e) => setCapacity(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="rounded-pill"
                />
              </div>
              <div className="col">
                <Form.Control
                  type="text"
                  placeholder="販売元名"
                  value={manufacturer}
                  onChange={(e) => setManufacturer(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="rounded-pill"
                />
              </div>
              <div className="col">
                <Form.Control
                  type="text"
                  placeholder="一般名"
                  value={genericName}
                  onChange={(e) => setGenericName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="rounded-pill"
                />
              </div>
              <div className="col-2 d-flex align-items-center">
                <div style={{ display: 'flex', alignItems: 'center', marginRight: '1em' }}>
                  <Form.Check
                    type="radio"
                    id="search-operator-and-q"
                    name="searchOperator"
                    defaultChecked
                    onChange={() => setSearchOperation('and')}
                  />
                  <label htmlFor="search-operator-and-q" className="ms-2">
                    and
                  </label>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', marginRight: '1em' }}>
                  <Form.Check
                    type="radio"
                    id="search-operator-or-q"
                    name="searchOperator"
                    onChange={() => setSearchOperation('or')}
                  />
                  <label htmlFor="search-operator-or-q" className="ms-2">
                    or
                  </label>
                </div>
              </div>
            </div>

            <div className="row g-3 mb-3 align-items-center">
              <div className="col">
                <Form.Control
                  type="text"
                  placeholder="JANコード"
                  value={janCode}
                  onChange={(e) => setJanCode(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="rounded-pill"
                />
              </div>
              <div className="col">
                <Form.Control
                  type="text"
                  placeholder="YJコード"
                  value={yjCode}
                  onChange={(e) => setYjCode(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="rounded-pill"
                />
              </div>
              <div className="col">
                <Form.Control
                  type="text"
                  placeholder="GS1コード"
                  value={gs1Code}
                  onChange={(e) => setGs1Code(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="rounded-pill"
                />
              </div>
              <div className="col">
                <Form.Control
                  type="text"
                  placeholder="薬効分類"
                  value={medicineCategory}
                  onChange={(e) => setMedicineCategory(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="rounded-pill"
                />
              </div>
              <div className="col-2">
                <Button className="w-100 bg-success rounded-pill" onClick={handleSearch}>
                  検索実行
                </Button>
              </div>
            </div>

            <div className="d-flex flex-wrap align-items-center mt-3">
              {/*<div className="d-flex align-items-center gap-2">*/}
              <Form.Check
                className="me-5 d-flex align-items-center gap-2 d-flex-center-label"
                inline
                label="内"
                onChange={(e) => {
                  onTypeChange('内', e.target.checked);
                }}
                defaultChecked
              />
              <Form.Check
                className="me-5 d-flex align-items-center gap-2 d-flex-center-label"
                inline
                label="外"
                onChange={(e) => {
                  onTypeChange('外', e.target.checked);
                }}
                defaultChecked
              />
              <Form.Check
                className="me-5 d-flex align-items-center gap-2 d-flex-center-label"
                inline
                label="注"
                onChange={(e) => {
                  onTypeChange('注', e.target.checked);
                }}
                defaultChecked
              />
              <Form.Check
                className="me-5 d-flex align-items-center gap-2 d-flex-center-label"
                inline
                label="歯"
                onChange={(e) => {
                  onTypeChange('歯', e.target.checked);
                }}
              />
              {/*<Form.Check*/}
              {/*  inline*/}
              {/*  id="exclude-processed"*/}
              {/*  label="経過措置を対象外にする"*/}
              {/*  checked={excludeProcessed}*/}
              {/*  onChange={(e) => setExcludeProcessed(e.target.checked)}*/}
              {/*  className="me-4"*/}
              {/*/>*/}
              {/*</div>*/}

              {/*<div>*/}
              {/*  <Button*/}
              {/*    className="me-5 d-flex align-items-center gap-2"*/}
              {/*    variant="secondary"*/}
              {/*    onClick={() => setShowModal('addDetailModal')}*/}
              {/*  >*/}
              {/*    <Plus className="fs-2 me-5" style={{ height: '1em' }} />*/}
              {/*    <span className="me-5">検索結果にない場合はこちら</span>*/}
              {/*  </Button>*/}
              {/*</div>*/}
            </div>
          </div>
          {/*</div>*/}
        </Col>
      </Row>

      <Row className="mt-2">
        <Col className="d-flex justify-content-end">
          <Form.Select
            style={{ width: '12rem' }}
            value={sortOrder}
            onChange={(e) => handleSortOrderChange(e.target.value as SortOrder)} // ★ onChangeでhandleSortOrderChangeを呼び出す
          >
            <option value="DESC">依頼日: 最新順</option>
            <option value="ASC">依頼日: 古い順</option>
            <option value="STATUS">ステータス順</option>
          </Form.Select>
        </Col>
      </Row>

      <Row className="mt-2">
        <Col>{error && <Alert variant="danger">{graphQlErrorMessage(error)}</Alert>}</Col>
      </Row>
      <Row>
        <Table className="table-borderless">
          <thead className="text-black-50 border-bottom border-success border-3">
            <tr>
              <th rowSpan={2} className="align-middle" style={{ width: '8rem' }}>
                依頼日
              </th>
              <th rowSpan={2} className="align-middle" style={{ width: '8rem' }}>
                希望回答日
              </th>
              <th rowSpan={2} className="align-middle" style={{ width: '8rem' }}>
                状況
              </th>
              <th>見積No.</th>
              <th>主な品名</th>
            </tr>
            <tr>
              <th>ご要望など</th>
            </tr>
          </thead>
          {quotations.map((quotation) => {
            return (
              <tbody key={quotation.quotationId}>
                <tr>
                  <td rowSpan={2} className="align-middle text-nowrap">
                    {parseISODateAndformatDate(quotation.requestAt)}
                  </td>
                  <td rowSpan={2} className="align-middle text-nowrap">
                    {parseISODateAndformatDate(quotation.answerAt)}
                  </td>
                  <td rowSpan={2} className="align-middle text-nowrap">
                    <span
                      className="py-1 px-2"
                      style={{
                        backgroundColor: quotation.quotationStatus === '完了' ? '#F2F2F2' : 'white',
                      }}
                    >
                      {quotation.quotationStatus}
                    </span>
                  </td>
                  <td>
                    <Link
                      to={`${QUOTATIONS_PATH}/${quotation.quotationId}`}
                      className="text-success text-decoration-none"
                    >
                      NO. {quotation.quotationNo}
                    </Link>
                  </td>
                  <td>{detailSummary(quotation.summary)}</td>
                  <td rowSpan={2} className="align-middle text-nowrap">
                    {quotation.pdfUrl && (
                      <a
                        target="_blank"
                        href={`${quotation.pdfUrl}`}
                        className="text-success text-decoration-none"
                        rel="noreferrer"
                      >
                        <Button variant="danger">PDF</Button>
                      </a>
                    )}
                  </td>
                </tr>
                <tr className="border-bottom">
                  <td
                    colSpan={2}
                    className="text-secondary text-truncate text-pre-wrap"
                    style={{ maxWidth: '15rem' }}
                  >
                    {quotation.note}
                  </td>
                </tr>
              </tbody>
            );
          })}
          <tfoot className="border-top border-success border-3">
            <tr></tr>
          </tfoot>
        </Table>
        <PaginationComponent
          total={data?.quotations?.count ?? 0}
          itemsPerPage={paging.pageSize}
          currentPage={paging.page + 1}
          pageSize={paging.pageSize}
          onPageChange={(page) => setSessionPaging(page - 1, paging.pageSize)}
          onPageSize={(pageSize) => setSessionPaging(0, pageSize)}
        />
      </Row>
      {loading && <Loading />}
    </Container>
  );
};

export default QuotationList;
